import React, { type PropsWithChildren, type ReactNode } from 'react';

import { defineMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import { Bleed } from '@atlaskit/primitives/compiled/bleed';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';

import type { ActionRenderMode } from '../../../common/convertActionStatusToRenderMode';
import { SystemMessage } from '../../../common/ui/system-message/SystemMessage';

const planDismissedMessage = defineMessage({
	id: 'ai-mate-jira-admin-agent.jira-admin-agent.action-cards.ActionCardFooter.planDismissed.non-final',
	defaultMessage: "You've dismissed this plan",
	description:
		'Status pill shown in the action card footer (in place of the action buttons) after the user dismisses the proposed plan, confirming that no actions were applied.',
});

const dismissMessage = defineMessage({
	id: 'ai-mate-jira-admin-agent.jira-admin-agent.action-cards.ActionCardFooter.dismiss',
	defaultMessage: 'Cancel',
	description:
		'Button label displayed in the footer of an action card within the AI conversation interface. Shown as clickable text on a subtle button positioned on the left of the footer. When clicked, it dismisses the action card without taking any action on the proposed plan. The purpose is to allow users to discard the suggested plan when they do not wish to proceed with it.',
});

const openPlanMessage = defineMessage({
	id: 'ai-mate-jira-admin-agent.jira-admin-agent.action-cards.ActionCardFooter.openPlan',
	defaultMessage: 'View details',
	description:
		'Button label displayed in the footer of an action card within the AI conversation interface. Shown as clickable text on a button positioned on the right of the footer. When clicked, it opens the proposed plan so the user can review it in more detail. The purpose is to allow users to inspect the full plan before deciding whether to accept it.',
});

const acceptPlanMessage = defineMessage({
	id: 'ai-mate-jira-admin-agent.jira-admin-agent.action-cards.ActionCardFooter.acceptPlan',
	defaultMessage: 'Apply changes',
	description:
		'Button label displayed in the footer of an action card within the AI conversation interface. Shown as clickable text on a primary (highlighted) button positioned on the far right of the footer. When clicked, it accepts the proposed plan and proceeds with it. The purpose is to allow users to confirm and apply the suggested plan.',
});

const completedMessage = defineMessage({
	id: 'ai-mate-jira-admin-agent.jira-admin-agent.action-cards.ActionCardFooter.completed.non-final',
	defaultMessage: 'Completed',
	description:
		'Label shown in place of the Accept plan button once a plan step has been successfully applied.',
});

const failedMessage = defineMessage({
	id: 'ai-mate-jira-admin-agent.jira-admin-agent.action-cards.ActionCardFooter.failed.non-final',
	defaultMessage: "Couldn't apply",
	description: 'Message shown next to a plan when applying that plan failed; the user can retry.',
});

const retryMessage = defineMessage({
	id: 'ai-mate-jira-admin-agent.jira-admin-agent.action-cards.ActionCardFooter.retry.non-final',
	defaultMessage: 'Retry',
	description:
		'Label for the button that re-attempts applying a plan after a previous attempt failed.',
});

const styles = cssMap({
	actionCardFooter: {
		borderTopColor: token('color.border'),
		borderTopStyle: 'solid',
		borderTopWidth: token('border.width'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	actionCardFooterOuterMargin: {
		marginTop: token('space.150'),
		marginBottom: token('space.negative.150'),
	},
});

type ActionCardFooterProps = PropsWithChildren<{
	renderMode: ActionRenderMode;
	testId?: string;
	/** Whether the canvas (Rovodal) is currently open. When `true` (and loaded), the footer buttons are hidden. */
	isCanvasOpen?: boolean;
	/** Whether the canvas (Rovodal) has been opened but is still loading. Shows a loading state on "Open plan". */
	isCanvasLoading?: boolean;
	/** Fired when the user accepts the plan (primary "Accept plan" button). */
	onAcceptPlan: () => void;
	/** Fired when the user dismisses the plan (subtle "Dismiss" button). */
	onDismissPlan: () => void;
	/** Fired when the user reviews the plan (default "Open plan" button). */
	onReviewPlan: () => void;
}>;

export const ActionCardFooter = ({
	children,
	testId,
	isCanvasOpen = false,
	isCanvasLoading = false,
	renderMode,
	onAcceptPlan,
	onDismissPlan,
	onReviewPlan,
}: ActionCardFooterProps): ReactNode => {
	const { formatMessage } = useIntl();

	if (isCanvasOpen && !isCanvasLoading) {
		return null;
	}

	// A dismissed plan replaces all footer actions with the dismissed pill, while the plan details
	// above remain visible so the user can still see what was discarded.
	if (renderMode === 'CANCELLED') {
		return (
			<Bleed inline="space.150" xcss={styles.actionCardFooterOuterMargin}>
				<Box xcss={styles.actionCardFooter} testId={testId}>
					<SystemMessage message={formatMessage(planDismissedMessage)} isSuccessMessage={false} />
				</Box>
			</Bleed>
		);
	}

	return (
		<Bleed inline="space.150" xcss={styles.actionCardFooterOuterMargin}>
			<Box xcss={styles.actionCardFooter} testId={testId}>
				{renderMode === 'IN_PROGRESS' ? (
					<Spinner interactionName="load" />
				) : (
					<Inline space="space.100" alignBlock="center" spread="space-between">
						<Button appearance="subtle" onClick={onDismissPlan} isDisabled={isCanvasLoading}>
							{formatMessage(dismissMessage)}
						</Button>
						<Inline space="space.100" alignBlock="center">
							<Button appearance="default" onClick={onReviewPlan} isLoading={isCanvasLoading}>
								{formatMessage(openPlanMessage)}
							</Button>
							{renderMode === 'ERROR' && (
								<Inline space="space.100" alignBlock="center">
									<CrossCircleIcon label="" color={token('color.icon.danger')} />
									<Text size="small" color="color.text.danger">
										{formatMessage(failedMessage)}
									</Text>
									<Button appearance="default" onClick={onAcceptPlan}>
										{formatMessage(retryMessage)}
									</Button>
								</Inline>
							)}
							{renderMode === 'COMPLETE' && (
								<Button appearance="subtle" iconBefore={CheckMarkIcon} isDisabled>
									{formatMessage(completedMessage)}
								</Button>
							)}
							{renderMode === 'IDLE' && (
								<Button appearance="primary" onClick={onAcceptPlan} isDisabled={isCanvasLoading}>
									{formatMessage(acceptPlanMessage)}
								</Button>
							)}
						</Inline>
					</Inline>
				)}
				{children}
			</Box>
		</Bleed>
	);
};
